import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import { ENewsletterType } from '../../models/newsletter.model';
import NewsletterTypeSelect from '../NewsletterTypeSelect';
import { ContentEditor } from '../ContentEditor';
import newsletterApi from '../../api/newsletter.api';

interface SendNewsletterModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const SendNewsletterModal: React.FC<SendNewsletterModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    subject_en: '',
    subject_vi: '',
    newsletter_type: null as ENewsletterType | null,
  });
  const [contentEn, setContentEn] = useState('');
  const [contentVi, setContentVi] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const contentEditorRef = useRef<ContentEditor>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setFormData({
        subject_en: '',
        subject_vi: '',
        newsletter_type: null,
      });
      setShowConfirmation(false);
      setContentEn('');
      setContentVi('');
    }
  }, [open]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.subject_en.trim()) {
      toast.error('English subject is required');
      return false;
    }
    if (!formData.subject_vi.trim()) {
      toast.error('Vietnamese subject is required');
      return false;
    }
    if (!formData.newsletter_type) {
      toast.error('Newsletter type is required');
      return false;
    }

    if (showConfirmation) {
      if (!contentEn.trim() || !contentVi.trim()) {
        toast.error('Both English and Vietnamese content are required');
        return false;
      }
    } else {
      if (
        !contentEditorRef.current?.getContentData()?.content_en ||
        !contentEditorRef.current?.getContentData()?.content_vi
      ) {
        toast.error('Both English and Vietnamese content are required');
        return false;
      }
    }

    return true;
  };

  const handleSend = async () => {
    if (contentEditorRef?.current?.getContentData()) {
      const contentData = contentEditorRef.current?.getContentData();
      console.log(contentData);
      setContentEn(contentData!.content_en);
      setContentVi(contentData!.content_vi);
    }

    if (!validateForm()) return;

    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    setLoading(true);
    try {
      await newsletterApi.create({
        subject_en: formData.subject_en,
        subject_vi: formData.subject_vi,
        newsletter_type: formData.newsletter_type!,
        content_en: contentEn,
        content_vi: contentVi,
      });

      setContentEn('');
      setContentVi('');
      toast.success('Newsletter sent successfully');
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send newsletter');
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const handleCancel = () => {
    if (showConfirmation) {
      setShowConfirmation(false);
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' },
      }}
    >
      <DialogTitle>
        {showConfirmation ? 'Confirm Send Newsletter' : 'Send New Newsletter'}
      </DialogTitle>

      <DialogContent dividers>
        {showConfirmation ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Are you sure you want to send this newsletter?
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              This newsletter will be sent to all users subscribed to{' '}
              <strong>{formData.newsletter_type}</strong> newsletters.
            </Typography>
            <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Subject
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Subject (English)"
                fullWidth
                size="small"
                required
                value={formData.subject_en}
                onChange={(e) =>
                  handleInputChange('subject_en', e.target.value)
                }
                inputProps={{ maxLength: 255 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Subject (Vietnamese)"
                fullWidth
                size="small"
                required
                value={formData.subject_vi}
                onChange={(e) =>
                  handleInputChange('subject_vi', e.target.value)
                }
                inputProps={{ maxLength: 255 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <NewsletterTypeSelect
                label="Newsletter Type"
                required
                value={formData.newsletter_type}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, newsletter_type: value }))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <ContentEditor
                ref={contentEditorRef}
                initData={{
                  content_en: contentEn,
                  content_vi: contentVi,
                }}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          {showConfirmation ? 'Back' : 'Cancel'}
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {showConfirmation ? 'Confirm & Send' : 'Send Newsletter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendNewsletterModal;
